import { Component, Host, h, Element, State, Prop } from '@stencil/core';
import { handlePromoView, handleCtaClick } from '@aha-app/aha-shared';
import { Size } from './types';

export interface AhaPromoGrayCallout extends HTMLAhaPromoGrayCalloutElement {}

/**
 * Gray callout box used for cross-promos.
 * @slot title - The title text in the callout box
 */
@Component({
  tag: 'aha-promo-gray-callout',
  styleUrl: 'aha-promo-gray-callout.css',
  shadow: true,
})
export class AhaPromoGrayCallout {
  @Element() host!: HTMLAhaPromoGrayCalloutElement;

  /**
   * The path of image to display.
   */
  @Prop() imagePath!: string;

  /**
   * The snake case name of the upgrade location, found in SuiteHelper::ANALYTICS_TRACKING_PROPS.
   * The upgrade location is used to infer the promoted product, which then informs the feature ID and CTA link path.
   */
  @Prop() upgradeLocation!: string;

  /**
   * Optional anchor link for the callout.
   */
  @Prop() anchor?: string;

  /**
   * Optionally set the width of the callout.
   */
  @Prop() width?: string;

  /**
   * Optionally set the font sizing.
   */
  @Prop() fontSizing: Size = 'default';

  private observer: IntersectionObserver | null = null;
  private hasBeenViewed = false;

  componentDidLoad() {
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  private setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !this.hasBeenViewed) {
          this.hasBeenViewed = true;
          handlePromoView(this.upgradeLocation);
          this.observer?.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    this.observer.observe(this.host);
  }

  render() {
    return (
      <Host>
        <div class="callout" style={{ width: this.width || '' }}>
          <img src={this.imagePath} />
          <div class="content">
            <div class={`header ${this.fontSizing === 'small' ? 'header--small' : ''}`}>
              <slot name="title"></slot>
              <aha-promo-try-pill font-sizing="medium" pill-size="small" />
            </div>
            <div class={`description ${this.fontSizing === 'small' ? 'description--small' : ''}`}>
              <slot></slot>
            </div>
            <aha-button
              kind="success"
              onClick={e => handleCtaClick(e, this.upgradeLocation, this.anchor)}
            >
              Try free
            </aha-button>
          </div>
        </div>
      </Host>
    );
  }
}
